import { useMemo } from "react";
import {
  AlertCircle,
  Ban,
  Box,
  CheckCircle2,
  Clock,
  Droplet,
  FileText,
  Gauge,
  Layers,
  MapPin,
  Pencil,
  Trash2,
  Truck,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/utils/cn";
import type {
  AbastecimentoDto,
  ContratoDto,
  FilaAprovacaoItem,
  LocacaoDto,
  LocacoesBiSummary,
  LocacoesDashboardDto,
  ManutencaoDto,
  MaquinarioDto,
  OperationalDeltasDto,
  OperationalMetricsDto,
  PeriodRange,
  RegistroHorimetroDto,
  FiltroTipoAtivo,
} from "@/services/locacoes";
import {
  CONTRATO_STATUS_LABELS,
  MANUTENCAO_STATUS_LABELS,
  MAQUINARIO_STATUS_LABELS,
  REGISTRO_HORIMETRO_LABELS,
} from "@/services/locacoes";
import { formatDate, formatNumber } from "@/utils/format";
import {
  formatContratoValor,
  locacoesListItemClass,
  locacoesListMetaClass,
  locacoesPanelClass,
  locacoesSectionTitleClass,
} from "./locacoesUtils";
import { LocacoesStatCard } from "./LocacoesStatCard";
import { MAQUINARIO_STATUS_THEME } from "./statusTheme";
import { buildMaquinasOperacao, computeAbastecimentosResumoPeriodo, filterAbastecimentosByPeriod, filterAbastecimentosContratosAtivos, filterRegistrosByPeriod, formatHoras, resolveMaquinaNome } from "./locacoesUtils";
import { buildLocacoesFinanceStats, LocacoesBiPanel } from "./LocacoesBiPanel";
import { EficienciaMaquinasPanel } from "./EficienciaMaquinasPanel";
import { FilaAprovacaoWidget } from "./FilaAprovacaoWidget";

interface Props {
  dashboard: LocacoesDashboardDto;
  metrics: OperationalMetricsDto;
  deltas: OperationalDeltasDto;
  biSummary?: LocacoesBiSummary | null;
  filaAprovacao?: FilaAprovacaoItem[];
  approvalBusy?: boolean;
  period: PeriodRange;
  /** Gráficos e KPIs operacionais só após filtro de escopo aplicado. */
  showCharts?: boolean;
  chartsLoading?: boolean;
  scopeLabel?: string | null;
  scopeContratoId?: string;
  scopeMaquinaId?: string;
  maquinarios: MaquinarioDto[];
  contratosAtivos: ContratoDto[];
  /** Contratos disponíveis nos filtros do gráfico de horas (pode incluir encerrados conforme escopo). */
  contratosFiltro?: ContratoDto[];
  manutencoesAbertas: ManutencaoDto[];
  locacoesAtivas?: LocacaoDto[];
  registrosHorimetro: RegistroHorimetroDto[];
  abastecimentos: AbastecimentoDto[];
  onNovaMaquina?: () => void;
  onEncerrarLocacao?: (locacao: LocacaoDto) => void;
  onCancelarLocacao?: (locacao: LocacaoDto) => void;
  onEditMaquina?: (m: MaquinarioDto) => void;
  onEditHorimetro?: (r: RegistroHorimetroDto) => void;
  onDeleteHorimetro?: (r: RegistroHorimetroDto) => void;
  onEditAbastecimento?: (a: AbastecimentoDto) => void;
  onDeleteAbastecimento?: (a: AbastecimentoDto) => void;
  onEditContrato?: (c: ContratoDto) => void;
  onAprovarFila?: (item: FilaAprovacaoItem) => Promise<void>;
  onRejeitarFila?: (item: FilaAprovacaoItem, motivo: string) => Promise<void>;
  onConferirFotosFila?: (item: FilaAprovacaoItem) => void;
  canApproveLocacoes?: boolean;
  operadorMode?: boolean;
  /** Filtro global de tipo de contrato (visão geral). */
  ativoModo?: FiltroTipoAtivo;
}

export function LocacoesDashboard({
  dashboard,
  metrics,
  deltas,
  period,
  showCharts = false,
  chartsLoading = false,
  scopeLabel,
  scopeContratoId,
  scopeMaquinaId,
  maquinarios,
  contratosAtivos,
  contratosFiltro,
  manutencoesAbertas,
  locacoesAtivas = [],
  registrosHorimetro,
  abastecimentos,
  onNovaMaquina,
  onEncerrarLocacao,
  onCancelarLocacao,
  onEditMaquina,
  onEditHorimetro,
  onDeleteHorimetro,
  onEditAbastecimento,
  onDeleteAbastecimento,
  onEditContrato,
  biSummary,
  filaAprovacao = [],
  approvalBusy,
  onAprovarFila,
  onRejeitarFila,
  onConferirFotosFila,
  canApproveLocacoes = false,
  operadorMode = false,
  ativoModo = "todos",
}: Props) {
  const isEquipamentoModo = ativoModo === "equipamento";
  const useListFleet = ativoModo !== "todos";

  const fleetFromList = useMemo(() => {
    const total = maquinarios.length;
    const disponiveis = maquinarios.filter((m) => m.status === "disponivel").length;
    const locadas = maquinarios.filter((m) => m.status === "locado").length;
    const emManutencao = maquinarios.filter((m) => m.status === "manutencao").length;
    const inativas = maquinarios.filter((m) => m.status === "inativo").length;
    return {
      totalMaquinas: total,
      disponiveis,
      locadas,
      emManutencao,
      inativas,
      maquinasAtivas: total - inativas,
    };
  }, [maquinarios]);

  const fleet = useListFleet ? fleetFromList : dashboard;

  const biFiltered = useMemo(() => {
    if (!biSummary || !isEquipamentoModo) return biSummary;
    const ids = new Set(contratosAtivos.map((c) => c.id));
    const rentabilidadePorContrato = biSummary.rentabilidadePorContrato.filter((r) => ids.has(r.contratoId));
    const faturamentoTotal = rentabilidadePorContrato.reduce((s, r) => s + r.faturamento, 0);
    const gastosTotal = rentabilidadePorContrato.reduce((s, r) => s + r.gastos, 0);
    const margem = faturamentoTotal - gastosTotal;
    const margemPercentual =
      faturamentoTotal > 0 ? Math.round((margem / faturamentoTotal) * 1000) / 10 : 0;
    return {
      ...biSummary,
      faturamentoTotal,
      gastosTotal,
      gastosCombustivel: gastosTotal,
      gastosManutencao: 0,
      margem,
      margemPercentual,
      rentabilidadePorContrato,
      eficienciaMaquinas: [],
    };
  }, [biSummary, isEquipamentoModo, contratosAtivos]);

  const maquinaIds = useMemo(() => new Set(maquinarios.map((m) => m.id)), [maquinarios]);
  const registrosNoPeriodo = filterRegistrosByPeriod(registrosHorimetro, period).filter((r) =>
    maquinaIds.has(r.maquinaId),
  );

  const abastBase = useMemo(() => {
    if (scopeLabel) return abastecimentos;
    return filterAbastecimentosContratosAtivos(contratosAtivos, abastecimentos);
  }, [abastecimentos, contratosAtivos, scopeLabel]);

  const abastResumo = useMemo(
    () => computeAbastecimentosResumoPeriodo(abastBase, period),
    [abastBase, period],
  );

  const abastNoPeriodo = useMemo(
    () =>
      filterAbastecimentosByPeriod(abastBase, period).filter((a) =>
        scopeLabel ? maquinaIds.has(a.maquinaId) : true,
      ),
    [abastBase, period, scopeLabel, maquinaIds],
  );

  const abastOrfaosGaragem = useMemo(() => {
    if (scopeLabel) return 0;
    const vinculados = new Set(filterAbastecimentosContratosAtivos(contratosAtivos, abastecimentos).map((a) => a.id));
    return filterAbastecimentosByPeriod(abastecimentos, period).filter((a) => !vinculados.has(a.id)).length;
  }, [abastecimentos, contratosAtivos, period, scopeLabel]);

  /** Com filtro de maquinário, status de frota (disponível/locado/inativo) não faz sentido. */
  const hasMaquinaFilter = Boolean(scopeMaquinaId);

  const fleetStats = [
    {
      title: isEquipamentoModo ? "Total equipamentos" : "Total máquinas",
      value: fleet.totalMaquinas,
      icon: isEquipamentoModo ? <Box className="h-5 w-5" /> : <Truck className="h-5 w-5" />,
      accentClass: "border-l-4 border-l-blue-400",
    },
    ...(!hasMaquinaFilter
      ? [
          {
            title: "Disponíveis",
            value: fleet.disponiveis,
            icon: <CheckCircle2 className="h-5 w-5" />,
            accentClass: "border-l-4 border-l-emerald-400",
          },
          {
            title: "Locados",
            value: fleet.locadas,
            icon: <MapPin className="h-5 w-5" />,
            accentClass: "border-l-4 border-l-sky-400",
          },
        ]
      : []),
    {
      title: "Em manutenção",
      value: fleet.emManutencao,
      icon: <Wrench className="h-5 w-5" />,
      accentClass: "border-l-4 border-l-amber-400",
    },
    ...(!hasMaquinaFilter
      ? [
          {
            title: "Inativos",
            value: fleet.inativas,
            icon: <Ban className="h-5 w-5" />,
            accentClass: "border-l-4 border-l-rose-400",
          },
        ]
      : []),
  ];

  const tiposCadastrados = useMemo(
    () => new Set(maquinarios.map((m) => m.tipo).filter(Boolean)).size,
    [maquinarios],
  );
  const taxaOcupacao =
    fleet.totalMaquinas > 0 ? Math.round((fleet.locadas / fleet.totalMaquinas) * 100) : 0;

  const equipamentoOpStats = [
    {
      title: "Taxa de ocupação",
      value: `${taxaOcupacao}%`,
      subtitle: `${fleet.locadas} de ${fleet.totalMaquinas} locados`,
      icon: <Gauge className="h-5 w-5" />,
      accentClass: "border-l-4 border-l-purple-400",
    },
    {
      title: "Tipos cadastrados",
      value: tiposCadastrados,
      icon: <Layers className="h-5 w-5" />,
      accentClass: "border-l-4 border-l-teal-400",
    },
  ];

  const opStats = [
    {
      title: "Horas trabalhadas",
      value: formatHoras(metrics.horasTrabalhadas),
      variation: deltas.horasTrabalhadas,
      subtitle:
        metrics.horasTrabalhadas === 0 && registrosNoPeriodo.length > 0
          ? "Registre o horímetro final para calcular horas"
          : undefined,
      icon: <Clock className="h-5 w-5" />,
      accentClass: "border-l-4 border-l-emerald-400",
    },
    {
      title: "Abastecimentos",
      value: abastResumo.count,
      variation: abastResumo.deltaCount,
      subtitle:
        abastOrfaosGaragem > 0
          ? `${abastOrfaosGaragem} na garagem (fora dos contratos ativos)`
          : undefined,
      icon: <Droplet className="h-5 w-5" />,
      accentClass: "border-l-4 border-l-sky-400",
    },
    {
      title: "Litros abastecidos",
      value: `${formatNumber(abastResumo.litros, 0)} L`,
      variation: abastResumo.deltaLitros,
      icon: <Gauge className="h-5 w-5" />,
      accentClass: "border-l-4 border-l-orange-400",
    },
    {
      title: "Consumo médio",
      value: `${formatNumber(abastResumo.consumoMedio || metrics.consumoMedioGeral, 1)} L/h`,
      variation: deltas.consumoMedio,
      icon: <Gauge className="h-5 w-5" />,
      accentClass: "border-l-4 border-l-purple-400",
    },
  ];

  const maquinasOp = buildMaquinasOperacao(maquinarios, registrosHorimetro, period);
  const recentHorimetro = [...registrosNoPeriodo]
    .sort((a, b) => b.dataHora.localeCompare(a.dataHora))
    .slice(0, 6);
  const recentAbast = [...abastNoPeriodo].sort((a, b) => b.data.localeCompare(a.data)).slice(0, 6);

  const financeStats = biFiltered ? buildLocacoesFinanceStats(biFiltered, isEquipamentoModo) : [];
  /** Com filtro de máquina + gráficos: unifica frota + operação + financeiro em 3×3. */
  const useMaquinaStatsGrid = hasMaquinaFilter && showCharts && !isEquipamentoModo;

  return (
    <div className="flex flex-col gap-6">
      {scopeLabel && (
        <div className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-900">
          Exibindo dados filtrados: <strong>{scopeLabel}</strong>
        </div>
      )}

      {useMaquinaStatsGrid ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...fleetStats, ...opStats, ...financeStats].map((s) => (
            <LocacoesStatCard key={s.title} {...s} />
          ))}
        </div>
      ) : (
        <div
          className={
            hasMaquinaFilter
              ? "grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-2"
              : "grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5"
          }
        >
          {fleetStats.map((s) => (
            <LocacoesStatCard key={s.title} {...s} />
          ))}
        </div>
      )}

      {!showCharts && !chartsLoading && (
        <div className="flex items-start gap-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm leading-relaxed text-slate-600">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-sky-500/80" aria-hidden />
          <span>
            Selecione <strong>período</strong> e pelo menos um filtro de <strong>cliente</strong>,{" "}
            <strong>contrato</strong> ou <strong>máquina/equipamento</strong>, depois clique em <strong>Aplicar</strong>{" "}
            para carregar rentabilidade, eficiência e demais gráficos do período.
          </span>
        </div>
      )}

      {chartsLoading && (
        <p className="py-3 text-center text-sm text-slate-400">Carregando gráficos do período…</p>
      )}

      {showCharts && (
        <>
      {!useMaquinaStatsGrid &&
        (isEquipamentoModo ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {equipamentoOpStats.map((s) => (
              <LocacoesStatCard key={s.title} {...s} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {opStats.map((s) => (
              <LocacoesStatCard key={s.title} {...s} />
            ))}
          </div>
        ))}

      {biFiltered && (
        <LocacoesBiPanel
          bi={biFiltered}
          modoEquipamento={isEquipamentoModo}
          hideRentabilidadeChart={hasMaquinaFilter}
          hideFinanceStats={useMaquinaStatsGrid}
        />
      )}

      {!isEquipamentoModo && (
        <EficienciaMaquinasPanel
          rows={metrics.consumoPorMaquina}
          maquinarios={maquinarios}
          contratos={contratosFiltro ?? contratosAtivos}
          maquinasOperacao={maquinasOp}
          registrosHorimetro={registrosHorimetro}
          period={period}
          scopeContratoId={scopeContratoId}
          scopeMaquinaId={scopeMaquinaId}
          onNovaMaquina={onNovaMaquina}
          hideComparativoCharts={hasMaquinaFilter}
        />
      )}

      {!isEquipamentoModo && !operadorMode && (filaAprovacao.length > 0 || onAprovarFila) && (
        <FilaAprovacaoWidget
          items={filaAprovacao}
          busy={approvalBusy}
          canApprove={canApproveLocacoes}
          onAprovar={onAprovarFila ?? (async () => {})}
          onRejeitar={onRejeitarFila ?? (async () => {})}
          onConferirFotos={onConferirFotosFila}
        />
      )}

      {!isEquipamentoModo && (
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-6">
        <div className={locacoesPanelClass}>
          <h3 className={locacoesSectionTitleClass}>
            <Clock className="h-4 w-4 text-emerald-500" />
            Registros recentes — Horímetro
          </h3>
          {recentHorimetro.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum registro ainda.</p>
          ) : (
            <ul className="divide-y divide-slate-100/90">
              {recentHorimetro.map((r) => (
                <li key={r.id} className={locacoesListItemClass}>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800">
                      {resolveMaquinaNome(maquinarios, r.maquinaId, r.maquinaNome)}
                    </p>
                    <p className={locacoesListMetaClass}>
                      {REGISTRO_HORIMETRO_LABELS[r.tipo]} • {formatDate(r.dataHora.slice(0, 10))} •{" "}
                      {r.horimetroInformado} h • {r.usuarioNome}
                    </p>
                  </div>
                  {(onEditHorimetro || onDeleteHorimetro) && (
                    <div className="flex shrink-0 gap-0.5">
                      {onEditHorimetro && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => onEditHorimetro(r)}
                          leftIcon={<Pencil className="h-3.5 w-3.5" />}
                          title="Editar"
                        >
                          <span className="sr-only">Editar</span>
                        </Button>
                      )}
                      {onDeleteHorimetro && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeleteHorimetro(r)}
                          leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                          title="Excluir"
                        >
                          <span className="sr-only">Excluir</span>
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={locacoesPanelClass}>
          <h3 className={locacoesSectionTitleClass}>
            <Droplet className="h-4 w-4 text-sky-500" />
            Abastecimentos recentes
          </h3>
          {recentAbast.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum abastecimento ainda.</p>
          ) : (
            <ul className="divide-y divide-slate-100/90">
              {recentAbast.map((a) => (
                <li key={a.id} className={locacoesListItemClass}>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800">
                      {resolveMaquinaNome(maquinarios, a.maquinaId, a.maquinaNome)}
                    </p>
                    <p className={locacoesListMetaClass}>
                      {formatNumber(a.litros, 0)} L • {formatDate(a.data)} • {a.usuarioNome}
                    </p>
                  </div>
                  {(onEditAbastecimento || onDeleteAbastecimento) && (
                    <div className="flex shrink-0 gap-0.5">
                      {onEditAbastecimento && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => onEditAbastecimento(a)}
                          leftIcon={<Pencil className="h-3.5 w-3.5" />}
                          title="Editar"
                        >
                          <span className="sr-only">Editar</span>
                        </Button>
                      )}
                      {onDeleteAbastecimento && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeleteAbastecimento(a)}
                          leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                          title="Excluir"
                        >
                          <span className="sr-only">Excluir</span>
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      )}

      {isEquipamentoModo && (
        <div className={locacoesPanelClass}>
          <h3 className={locacoesSectionTitleClass}>
            <Box className="h-4 w-4 text-brand-500" />
            Equipamentos cadastrados ({maquinarios.length})
          </h3>
          {maquinarios.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum equipamento cadastrado.</p>
          ) : (
            <ul className="divide-y divide-slate-100/90">
              {maquinarios.map((m) => {
                const theme = MAQUINARIO_STATUS_THEME[m.status];
                const locacao = locacoesAtivas.find((l) => l.maquinaId === m.id);
                return (
                  <li key={m.id} className={cn(locacoesListItemClass, "items-center")}>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-slate-800">{m.nome}</p>
                      <p className={cn(locacoesListMetaClass, "truncate")}>
                        {m.tipo || "—"} • {m.codigoInterno || "sem código"}
                        {locacao ? ` • ${locacao.costCenterLabel || locacao.obraNome || "Em locação"}` : ""}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold",
                          theme.badge,
                        )}
                      >
                        <span className={cn("h-1 w-1 rounded-full", theme.dot)} />
                        {MAQUINARIO_STATUS_LABELS[m.status]}
                      </span>
                      {onEditMaquina && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => onEditMaquina(m)}
                          leftIcon={<Pencil className="h-3.5 w-3.5" />}
                          title="Editar equipamento"
                        >
                          <span className="sr-only">Editar</span>
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

        </>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3 lg:gap-6">
        <div className={locacoesPanelClass}>
          <h3 className={locacoesSectionTitleClass}>
            <MapPin className="h-4 w-4 text-brand-500" />
            Locações ativas ({locacoesAtivas.length})
          </h3>
          {locacoesAtivas.length === 0 ? (
            <p className="text-sm text-slate-500">
              {isEquipamentoModo ? "Nenhum equipamento em locação no momento." : "Nenhuma máquina em locação no momento."}
            </p>
          ) : (
            <ul className="max-h-[22rem] divide-y divide-slate-100/90 overflow-y-auto pr-1">
              {locacoesAtivas.slice(0, 5).map((l) => (
                <li key={l.id} className={locacoesListItemClass}>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800">{l.maquinaNome}</p>
                    <p className={locacoesListMetaClass}>
                      {l.costCenterLabel || l.obraNome} • {l.responsavelNome}
                      {l.contratoLabel ? ` • ${l.contratoLabel}` : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-0.5">
                    {onEncerrarLocacao && (
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => onEncerrarLocacao(l)}>
                        Encerrar
                      </Button>
                    )}
                    {onCancelarLocacao && (
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-xs text-slate-500" onClick={() => onCancelarLocacao(l)}>
                        Cancelar
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={locacoesPanelClass}>
          <h3 className={locacoesSectionTitleClass}>
            <FileText className="h-4 w-4 text-sky-500" />
            Contratos ativos ({contratosAtivos.length})
          </h3>
          {contratosAtivos.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum contrato ativo.</p>
          ) : (
            <ul className="max-h-[22rem] divide-y divide-slate-100/90 overflow-y-auto pr-1">
              {contratosAtivos.map((c) => (
                <li key={c.id} className={locacoesListItemClass}>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800">{c.clienteSiengeNome}</p>
                    <p className={locacoesListMetaClass}>
                      {c.modeloContrato} • {formatContratoValor(c)} • {CONTRATO_STATUS_LABELS[c.status]}
                    </p>
                  </div>
                  {onEditContrato && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditContrato(c)}
                      leftIcon={<Pencil className="h-3.5 w-3.5" />}
                      title="Editar contrato"
                    >
                      <span className="sr-only">Editar</span>
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {!isEquipamentoModo && (
        <div className={locacoesPanelClass}>
          <h3 className={locacoesSectionTitleClass}>
            <Wrench className="h-4 w-4 text-amber-500" />
            Manutenções abertas ({manutencoesAbertas.length})
          </h3>
          {manutencoesAbertas.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhuma manutenção em aberto.</p>
          ) : (
            <ul className="max-h-[22rem] divide-y divide-slate-100/90 overflow-y-auto pr-1">
              {manutencoesAbertas.map((m) => (
                <li key={m.id} className={locacoesListItemClass}>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800">
                      {resolveMaquinaNome(maquinarios, m.maquinaId, m.maquinaNome)}
                    </p>
                    <p className={locacoesListMetaClass}>
                      {m.tipo} • {m.descricao}
                    </p>
                    <p className="mt-1 text-[11px] text-slate-400">{MANUTENCAO_STATUS_LABELS[m.status]}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
